<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Structured form that submits via fetch to a platform-internal endpoint,
 * unlike ContactFormBlock/NewsletterBlock, which POST to an external
 * third-party URL and never touch this platform. Supports a `file` field
 * type those two don't.
 *
 * `submit_url` must speak a tiny JSON contract: on success, respond 2xx with
 * `{ data: { message, redirect_url? } }`; on validation failure, respond 4xx
 * with `{ message, errors? }` (Laravel's default ValidationException shape).
 * Any backend that returns this — on this platform or another consuming
 * this layer — works here with zero block-side changes.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const fields = computed(() => props.data.fields ?? [])
const values = reactive<Record<string, string | boolean | File | null>>({})
const submitting = ref(false)
const submitted = ref(false)
const errorMessage = ref<string | null>(null)
const successMessage = ref<string | null>(null)

const inputClass
  = 'w-full rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-bg)] px-4 py-2.5 text-[var(--site-fg)]'

function onFileChange(name: string, event: Event) {
  const input = event.target as HTMLInputElement
  values[name] = input.files?.[0] ?? null
}

async function onSubmit() {
  if (!props.data.submit_url || submitting.value) return

  submitting.value = true
  errorMessage.value = null

  const formData = new FormData()
  for (const field of fields.value) {
    const value = values[field.name]
    if (value === undefined || value === null || value === '') continue
    formData.append(`answers[${field.name}]`, value instanceof File ? value : String(value))
  }

  try {
    const res = await fetch(props.data.submit_url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
    const body = await res.json().catch(() => ({}))

    if (!res.ok) {
      errorMessage.value = body?.message || props.data.error_text || 'Something went wrong — please try again.'
      return
    }

    submitted.value = true
    successMessage.value = body?.data?.message || props.data.success_text || 'Thanks — we have your request.'
    if (body?.data?.redirect_url) {
      window.location.href = body.data.redirect_url
    }
  } catch {
    errorMessage.value = props.data.error_text || 'Something went wrong — please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div
    v-if="data.submit_url"
    class="mx-auto max-w-xl"
  >
    <SiteSectionHeading
      :eyebrow="data.eyebrow"
      :title="data.headline"
      :subtitle="data.subtext"
    />

    <form
      v-if="!submitted"
      class="mt-6 space-y-4"
      @submit.prevent="onSubmit"
    >
      <div
        v-for="(field, i) in fields"
        :key="i"
      >
        <label
          v-if="field.type !== 'checkbox'"
          class="mb-1.5 block text-sm font-medium"
          :for="`f-${i}-${field.name}`"
        >
          {{ field.label }}<span
            v-if="field.required"
            aria-hidden="true"
          >&nbsp;*</span>
        </label>

        <textarea
          v-if="field.type === 'textarea'"
          :id="`f-${i}-${field.name}`"
          v-model="values[field.name] as string"
          :required="field.required"
          :placeholder="field.placeholder ?? ''"
          rows="5"
          :class="inputClass"
        />

        <select
          v-else-if="field.type === 'select'"
          :id="`f-${i}-${field.name}`"
          v-model="values[field.name] as string"
          :required="field.required"
          :class="inputClass"
        >
          <option
            value=""
            :disabled="field.required"
          >
            {{ field.placeholder || 'Choose…' }}
          </option>
          <option
            v-for="(option, oi) in field.options ?? []"
            :key="oi"
            :value="option"
          >
            {{ option }}
          </option>
        </select>

        <label
          v-else-if="field.type === 'checkbox'"
          class="flex items-start gap-2 text-sm"
        >
          <input
            :id="`f-${i}-${field.name}`"
            v-model="values[field.name] as boolean"
            type="checkbox"
            :required="field.required"
            class="mt-1 shrink-0"
          >
          <span>{{ field.label }}</span>
        </label>

        <input
          v-else-if="field.type === 'file'"
          :id="`f-${i}-${field.name}`"
          type="file"
          :required="field.required"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic"
          :class="inputClass"
          @change="onFileChange(field.name, $event)"
        >

        <input
          v-else
          :id="`f-${i}-${field.name}`"
          v-model="values[field.name] as string"
          :type="field.type"
          :required="field.required"
          :placeholder="field.placeholder ?? ''"
          :class="inputClass"
        >
      </div>

      <label
        v-if="data.consent_text"
        class="flex items-start gap-2 text-sm text-[var(--site-muted)]"
      >
        <input
          type="checkbox"
          required
          class="mt-1 shrink-0"
        >
        <span>{{ data.consent_text }}</span>
      </label>

      <p
        v-if="errorMessage"
        class="text-sm text-red-600"
      >
        {{ errorMessage }}
      </p>

      <button
        type="submit"
        class="site-btn-accent w-full px-6 py-2.5 font-semibold transition disabled:opacity-60"
        :disabled="submitting"
      >
        {{ submitting ? (data.submitting_text || 'Sending…') : (data.button_text || 'Send') }}
      </button>
    </form>

    <p
      v-else
      class="mt-3 text-center text-sm text-[var(--site-muted)]"
    >
      {{ successMessage }}
    </p>
  </div>
</template>
